import {
  detectGuestPartner,
  getGuestShopHomePath,
} from "../constants/guestPartnerPaths";
import { clearProductSearchPersistence } from "./localProductSearch";

export const GUEST_PRE_SEARCH_SCROLL_KEY = "guestPreSearchScrollY";
export const PRODUCT_SEARCH_QUERY_KEY = "q";

/**
 * Guest storefront routes where product search should stay on the same page
 * instead of navigating to /homeproducts.
 */
export const isInPlaceProductSearchPath = (pathname = "") => {
  if (!pathname) return false;
  if (pathname === "/guest" || pathname.startsWith("/guest/")) return true;
  if (detectGuestPartner(pathname)) return true;

  const shopHome = getGuestShopHomePath();
  if (
    shopHome &&
    shopHome !== "/guest" &&
    (pathname === shopHome || pathname.startsWith(`${shopHome}/`))
  ) {
    return true;
  }

  return false;
};

export const getProductSearchQuery = (search = "") => {
  try {
    const raw = new URLSearchParams(search).get(PRODUCT_SEARCH_QUERY_KEY);
    if (!raw) return "";
    return decodeURIComponent(raw).trim();
  } catch {
    return String(new URLSearchParams(search).get(PRODUCT_SEARCH_QUERY_KEY) || "")
      .split("%20")
      .join(" ")
      .trim();
  }
};

export const savePreSearchScrollY = (scrollY = 0) => {
  try {
    sessionStorage.setItem(GUEST_PRE_SEARCH_SCROLL_KEY, String(scrollY));
  } catch {
    // ignore storage errors
  }
};

export const readPreSearchScrollY = () => {
  try {
    const raw = sessionStorage.getItem(GUEST_PRE_SEARCH_SCROLL_KEY);
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : 0;
  } catch {
    return 0;
  }
};

export const clearPreSearchScrollY = () => {
  try {
    sessionStorage.removeItem(GUEST_PRE_SEARCH_SCROLL_KEY);
  } catch {
    // ignore
  }
};

/**
 * Prefer the current guest URL (keeps /guest/:pid) over bare /guest.
 */
export const resolveGuestStorefrontHomePath = (pathname = "") => {
  if (isInPlaceProductSearchPath(pathname)) {
    return pathname;
  }

  const shopHome = getGuestShopHomePath();
  if (shopHome && shopHome !== "/guest") return shopHome;

  try {
    const property = JSON.parse(localStorage.getItem("property") || "null") || {};
    const uniqueId = property.uniqueId;
    if (uniqueId) return `/guest/${uniqueId}`;
  } catch {
    // ignore
  }

  return shopHome || "/guest";
};

const stripSearchQuery = (search = "") => {
  const params = new URLSearchParams(search);
  params.delete(PRODUCT_SEARCH_QUERY_KEY);
  const next = params.toString();
  return next ? `?${next}` : "";
};

/**
 * Enter product search results without leaving the guest storefront when possible.
 * Callers should already have dispatched updateTerm / getProducts when needed.
 */
export const openProductSearch = (navigate, location, term, options = {}) => {
  const trimmed = String(term || "").trim();
  if (!trimmed || typeof navigate !== "function" || !location) return;

  if (location.pathname === "/homeproducts") {
    return;
  }

  const { replace: forceReplace = false, scrollToResults = true } = options;

  if (isInPlaceProductSearchPath(location.pathname)) {
    const alreadySearching = Boolean(getProductSearchQuery(location.search));
    const scrollY = alreadySearching
      ? location.state?.preSearchScrollY ?? readPreSearchScrollY()
      : window.scrollY || window.pageYOffset || 0;

    if (!alreadySearching) {
      savePreSearchScrollY(scrollY);
    }

    const params = new URLSearchParams(location.search);
    const currentQ = params.get(PRODUCT_SEARCH_QUERY_KEY);
    if (currentQ === trimmed && alreadySearching) {
      return;
    }
    params.set(PRODUCT_SEARCH_QUERY_KEY, trimmed);

    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      {
        replace: forceReplace || alreadySearching,
        state: {
          ...(location.state || {}),
          guestSearch: true,
          preSearchScrollY: scrollY,
        },
      }
    );

    if (scrollToResults) {
      window.scrollTo(0, 0);
    }
    return;
  }

  navigate("/homeproducts");
};

/**
 * Leave in-place search. Prefer history.back when we pushed a search entry.
 */
export const closeProductSearch = (navigate, location) => {
  if (typeof navigate !== "function" || !location) return;

  if (location.state?.guestSearch) {
    navigate(-1);
    return;
  }

  const scrollY =
    location.state?.preSearchScrollY ?? readPreSearchScrollY();
  navigate(
    {
      pathname: location.pathname,
      search: stripSearchQuery(location.search),
    },
    {
      replace: true,
      state: {
        ...(location.state || {}),
        guestSearch: false,
        restoreScrollY: scrollY,
      },
    }
  );
};

/**
 * Logo / Home: clear search persistence and return to the current guest
 * storefront without remounting onto a different path when possible.
 */
export const goGuestStorefrontHome = (
  navigate,
  location,
  { clearSearchInput, updateTerm } = {}
) => {
  if (typeof navigate !== "function" || !location) return;

  clearProductSearchPersistence();
  if (typeof clearSearchInput === "function") clearSearchInput();
  if (typeof updateTerm === "function") updateTerm("Food");

  const homePath = resolveGuestStorefrontHomePath(location.pathname);
  const hadSearch = Boolean(getProductSearchQuery(location.search));
  const scrollY =
    location.state?.preSearchScrollY ?? readPreSearchScrollY();

  if (hadSearch) {
    navigate(
      {
        pathname: homePath,
        search: stripSearchQuery(location.search),
      },
      {
        replace: true,
        state: {
          guestSearch: false,
          restoreScrollY: scrollY,
        },
      }
    );
    return;
  }

  if (location.pathname === homePath) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  navigate(homePath);
};

export const restorePreSearchScroll = (preferredScrollY) => {
  const scrollY =
    typeof preferredScrollY === "number" && Number.isFinite(preferredScrollY)
      ? preferredScrollY
      : readPreSearchScrollY();

  const apply = () => {
    window.scrollTo(0, scrollY);
  };

  // Layout may not be ready on the first frame after swapping views.
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(apply);
    window.setTimeout(apply, 50);
  });
};
